// client/src/views/EmbedMapView.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import InteractiveMap from './InteractiveMap';
import logo from '../assets/logo128x128.png';
import 'leaflet/dist/leaflet.css';
import './MapView.css';
import useStyles from '../components/styles/useStyles';

const MIN_LABEL_SCALE = 0.4;
const MAX_LABEL_SCALE = 1;
const DEFAULT_LABEL_SCALE = 0.6;

// Shared “Hide All / Show All” button style for POIs, Paths, Zones
function getToggleButtonStyle(mapStyles, fontFamily, overrides = {}) {
  return {
    background: mapStyles?.highlight_colour || '#ffe670',
    color: '#222',
    border: 'none',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 13,
    padding: '3px 11px',
    cursor: 'pointer',
    fontFamily,
    width: 100,
    ...overrides,
  };
}

function EmbedMap() {
  const { mapId } = useParams();
  const [mapData, setMapData] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [pois, setPois] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [customTypes, setCustomTypes] = useState({});
  const [typeColours, setTypeColours] = useState({});
  const [visibleTypes, setVisibleTypes] = useState(new Set());
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [hoveredType, setHoveredType] = useState(null);
  const [hoveredPoiId, setHoveredPoiId] = useState(null);

  const [showAllPoiLabels, setShowAllPoiLabels] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('showAllPoiLabels');
        if (stored != null) {
          return stored === 'true';
        }
      } catch {}
    }
    return true;
  });

  const [labelScale, setLabelScale] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('poiLabelScaleEmbed');
        if (stored != null) {
          const v = parseFloat(stored);
          if (!Number.isNaN(v) && v >= MIN_LABEL_SCALE && v <= MAX_LABEL_SCALE) {
            return v;
          }
        }
      } catch {}
    }
    return MIN_LABEL_SCALE;
  });

  const [zones, setZones] = useState([]);
  const [visibleZones, setVisibleZones] = useState(new Set());
  const [zonesExpanded, setZonesExpanded] = useState(false);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);

  const [pathsExpanded, setPathsExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [pathControls, setPathControls] = useState({});

  const {
    styles: mapStyles,
  } = useStyles(mapId, null);

  useEffect(() => {
    fetch(`/api/maps/${mapId}`)
      .then(res => res.json())
      .then(data => {
        setMapData(data);
        const img = new window.Image();
        img.onload = () => setImageSize([img.height, img.width]);
        img.src = data.image_url;
      });

    fetch(`/api/poi/all/${mapId}`)
      .then(res => res.json())
      .then(data => {
        const pois = Array.isArray(data.pois) ? data.pois : [];
        setPois(pois);

        const cats = new Set();
        const typesByCat = {};
        const colours = {};

        for (const poi of pois) {
          cats.add(poi.category);
          if (!typesByCat[poi.category]) typesByCat[poi.category] = [];
          if (!typesByCat[poi.category].includes(poi.type)) {
            typesByCat[poi.category].push(poi.type);
            colours[poi.type] = `#${(poi.color || 0).toString(16).padStart(6, '0')}`;
          }
        }

        for (const cat in typesByCat) {
          typesByCat[cat].sort((a, b) => a.localeCompare(b));
        }

        const sortedCats = Array.from(cats).sort((a, b) => a.localeCompare(b));
        setCustomCategories(sortedCats);
        setCustomTypes(typesByCat);
        setTypeColours(colours);
        setVisibleCategories(new Set(sortedCats));
        setVisibleTypes(new Set(Object.values(typesByCat).flat()));
        setExpandedCategories({});
        setExpandedTypes({});
      });

    fetch(`/api/polygons?mapId=${mapId}`)
      .then(res => res.json())
      .then(data => {
        setZones(data);
      })
      .catch(() => {
        setZones([]);
      });
  }, [mapId]);

  useEffect(() => {
    if (zones.length) {
      const allZoneIds = new Set(zones.map(zone => zone.id));
      const timer = setTimeout(() => {
        setVisibleZones(allZoneIds);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [zones]);

  const mainBgColour = mapStyles?.main_bg_colour || "#fff";
  const sidebarBgColour = mapStyles?.sidebar_bg_colour || "#222";
  const fontFamily = mapStyles?.font_family || "Inter, Arial, sans-serif";
  const fontColour = mapStyles?.font_colour || "#fff";

  useEffect(() => {
    document.documentElement.style.setProperty('--main-bg-colour', mainBgColour);
  }, [mainBgColour]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty(
        '--poi-label-scale',
        String(labelScale)
      );
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('poiLabelScaleEmbed', String(labelScale));
      } catch {}
    }
  }, [labelScale]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('showAllPoiLabels', showAllPoiLabels ? 'true' : 'false');
      } catch {}
    }
  }, [showAllPoiLabels]);

  const toggleType = (type) => {
    setVisibleTypes(prev => {
      const newSet = new Set(prev);
      newSet.has(type) ? newSet.delete(type) : newSet.add(type);
      return newSet;
    });
  };

  const toggleCategory = (category) => {
    setVisibleCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(category) ? newSet.delete(category) : newSet.add(category);
      return newSet;
    });
  };

  const toggleCategoryExpand = (category, e) => {
    e.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleTypeExpand = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const toggleAllTypes = () => {
    const allTypes = Object.values(customTypes).flat();
    if (visibleTypes.size === allTypes.length) {
      setVisibleTypes(new Set());
    } else {
      setVisibleTypes(new Set(allTypes));
    }
  };

  const toggleZone = (zoneId) => {
    setVisibleZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) newSet.delete(zoneId);
      else newSet.add(zoneId);
      return newSet;
    });
  };

  const pathZones = zones.filter(
    z =>
      (z.behavior_type || z.behaviorType || z.behavior) === 'pathing'
  );
  const normalZones = zones.filter(
    z =>
      (z.behavior_type || z.behaviorType || z.behavior) !== 'pathing'
  );

  const allZonesVisible =
    normalZones.length > 0 &&
    normalZones.every(z => visibleZones.has(z.id));

  // “All paths hidden” based on pathControls.hidden (for tokens)
  const allPathsHidden =
    pathZones.length > 0 &&
    pathZones.every(z => {
      const ctrl = pathControls[z.id] || {};
      return ctrl.hidden === true;
    });

  const toggleAllZones = () => {
    setVisibleZones(prev => {
      const currentAllZonesVisible =
        normalZones.length > 0 &&
        normalZones.every(z => prev.has(z.id));
      const next = new Set(prev);
      if (currentAllZonesVisible) {
        normalZones.forEach(z => next.delete(z.id));
      } else {
        normalZones.forEach(z => next.add(z.id));
      }
      return next;
    });
  };

  const toggleZonesExpand = () => {
    setZonesExpanded(prev => !prev);
  };

  // Per-path checkbox: toggle this path token’s hidden flag
  const togglePathVisibility = (zoneId) => {
    setPathControls(prev => {
      const prevForZone = prev[zoneId] || {};
      const currentlyHidden = !!prevForZone.hidden;
      return {
        ...prev,
        [zoneId]: {
          ...prevForZone,
          hidden: !currentlyHidden,
        },
      };
    });
  };

  const togglePathsExpanded = () => {
    setPathsExpanded(prev => !prev);
  };

  // “Hide/Show all paths” → flips pathControls.hidden for all path zones (tokens)
  const toggleAllPaths = () => {
    setPathControls(prev => {
      const nextHidden = !allPathsHidden;
      const next = { ...prev };
      pathZones.forEach(z => {
        const prevForZone = next[z.id] || {};
        next[z.id] = {
          ...prevForZone,
          hidden: nextHidden,
        };
      });
      return next;
    });
  };

  const handlePathControlChange = (zoneId, changes) => {
    setPathControls(prev => {
      const prevForZone = prev[zoneId] || {};
      return {
        ...prev,
        [zoneId]: {
          ...prevForZone,
          ...changes,
        },
      };
    });
  };

  if (!mapData || !imageSize) {
    return (
      <div
        style={{
          padding: '2rem',
          color: '#222',
          background: '#fff',
          textAlign: 'center',
        }}
      >
        Loading map…
      </div>
    );
  }

  const mapDisplayName = (mapStyles?.custom_map_title || mapData.name || '');
  const sectionNamePOIs = mapStyles?.section_name_pois || "POIs";
  const sectionNameZones = mapStyles?.section_name_zones || "Zones";
  const sectionNamePaths = mapStyles?.section_name_paths || "Paths";

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const hasSearch = normalizedSearch.length > 0;

  return (
    <div
      className="embed-sidebar"
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "row",
        background: mainBgColour,
        overflow: "hidden",
        position: "relative",
        fontFamily: fontFamily
      }}
    >
      <div
        className="sidebar"
        style={{
          background: sidebarBgColour,
          color: fontColour,
          padding: "0 0 0 0",
          borderRight: "1.5px solid #333",
          display: "flex",
          flexDirection: "column",
          minWidth: 100,
          zIndex: 12,
          height: "100vh",
          overflowY: "auto"
        }}
      >
        <div
          style={{
            padding: 18,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderBottom: "1px solid #393939"
          }}
        >
          <span
            style={{
              fontWeight: 800,
              fontSize: "1.15em",
              marginBottom: 7,
              color: mapStyles?.title_colour || "#ffe670",
              letterSpacing: "1px",
              fontFamily: fontFamily,
              textShadow: mapStyles?.title_shadow ? '0 2px 8px #000a' : undefined
            }}
          >
            {mapDisplayName}
          </span>
        </div>

        <div style={{ padding: 15 }}>
          <button
            style={{
              margin: '8px 0 6px 0',
              background: showAllPoiLabels ? (mapStyles?.highlight_colour || '#ffdc5e') : (sidebarBgColour),
              color: showAllPoiLabels ? sidebarBgColour : (fontColour),
              border: 'none',
              borderRadius: 8,
              padding: '7px 16px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              boxShadow: showAllPoiLabels ? '0 2px 14px #ffd70044' : 'none',
              transition: 'background 0.16s, color 0.16s',
              width: "100%",
              fontFamily: fontFamily
            }}
            onClick={() => setShowAllPoiLabels(v => !v)}
          >
            {showAllPoiLabels ? "Hide all POI labels" : "Show all POI labels"}
          </button>

          {showAllPoiLabels && (
            <div
              style={{
                marginTop: 6,
                marginBottom: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  userSelect: "none",
                  fontFamily: fontFamily,
                }}
              >
                POI label size
              </span>
              <input
                type="range"
                min={MIN_LABEL_SCALE}
                max={MAX_LABEL_SCALE}
                step="0.05"
                value={labelScale}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v)) {
                    const clamped =
                      v < MIN_LABEL_SCALE
                        ? MIN_LABEL_SCALE
                        : v > MAX_LABEL_SCALE
                        ? MAX_LABEL_SCALE
                        : v;
                    setLabelScale(clamped);
                  }
                }}
                style={{
                  width: "80%",
                  cursor: "pointer",
                }}
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 8,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontFamily,
                fontWeight: 700,
                fontSize: "1.05em",
              }}
              onClick={(e) => toggleCategoryExpand("__ALL__", e)}
            >
              <span
                style={{
                  marginRight: 7,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {'▼'}
              </span>
              <span>POIs</span>
            </div>
            <button
              onClick={toggleAllTypes}
              style={getToggleButtonStyle(mapStyles, fontFamily)}
            >
              {visibleTypes.size === Object.values(customTypes).flat().length
                ? "Hide All"
                : "Show All"}
            </button>
          </div>

          <div style={{ marginBottom: 10 }}>
            <input
              type="text"
              placeholder="Search POIs and types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 8px",
                borderRadius: 6,
                border: "1px solid #444",
                backgroundColor: "#18191c",
                color: "#fff",
                fontSize: 14,
                fontFamily: fontFamily
              }}
            />
          </div>

          {customCategories.map(category => {
            const categoryLabel = category || "—";
            const categoryMatchesSearch =
              hasSearch && categoryLabel.toLowerCase().includes(normalizedSearch);
            const typesInCategory = customTypes[category] || [];

            const typesToRender = typesInCategory.filter(type => {
              if (!hasSearch) return true;
              const typeMatchesSearch = type.toLowerCase().includes(normalizedSearch);
              const poisOfType = pois.filter(
                p => p.category === category && p.type === type
              );
              const hasMatchingPoi = poisOfType.some(poi => {
                const dn =
                  typeof poi.poi_name === "string"
                    ? poi.poi_name.toLowerCase()
                    : "";
                return dn.includes(normalizedSearch);
              });
              return categoryMatchesSearch || typeMatchesSearch || hasMatchingPoi;
            });

            if (hasSearch && !categoryMatchesSearch && typesToRender.length === 0) {
              return null;
            }

            const isCategoryExpanded = hasSearch
              ? true
              : !!expandedCategories[category];

            return (
              <div key={category}>
                <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <span
                    onClick={e => toggleCategoryExpand(category, e)}
                    style={{
                      marginRight: 5,
                      cursor: "pointer",
                      userSelect: "none",
                      fontWeight: 700,
                      fontSize: "1.1em",
                      fontFamily: fontFamily
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={isCategoryExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isCategoryExpanded ? '▼' : '►'}
                  </span>
                  <input
                    type="checkbox"
                    checked={visibleCategories.has(category)}
                    onChange={() => toggleCategory(category)}
                    style={{ marginRight: 5 }}
                  />
                  <strong style={{ fontFamily: fontFamily }}>{categoryLabel}</strong>
                </div>
                {isCategoryExpanded && (
                  <ul style={{ paddingLeft: 19 }}>
                    {typesToRender.map(type => {
                      const color = typeColours[type] || "#ff0000";
                      const poisOfType = pois.filter(
                        p => p.category === category && p.type === type
                      );

                      const matchingPois = hasSearch
                        ? poisOfType.filter(poi => {
                            const dn =
                              typeof poi.poi_name === "string"
                                ? poi.poi_name.toLowerCase()
                                : "";
                            return (
                              dn.includes(normalizedSearch) ||
                              type.toLowerCase().includes(normalizedSearch) ||
                              categoryLabel.toLowerCase().includes(normalizedSearch)
                            );
                          })
                        : poisOfType;

                      const count = matchingPois.length;
                      const isTypeExpanded = hasSearch
                        ? true
                        : !!expandedTypes[type];

                      return (
                        <li
                          key={type}
                          style={{ marginBottom: 2, fontFamily: fontFamily }}
                          onMouseEnter={() => setHoveredType(type)}
                          onMouseLeave={() => setHoveredType(null)}
                        >
                          <span
                            onClick={() => toggleTypeExpand(type)}
                            style={{ cursor: "pointer", marginRight: 6, userSelect: "none" }}
                            tabIndex={0}
                            role="button"
                            aria-label={isTypeExpanded ? "Collapse POIs" : "Expand POIs"}
                          >
                            {isTypeExpanded ? "▼" : "►"}
                          </span>
                          <input
                            type="checkbox"
                            checked={visibleTypes.has(type)}
                            onChange={() => toggleType(type)}
                          />
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              backgroundColor: color,
                              marginLeft: '6px',
                              marginRight: '6px',
                              borderRadius: '2px'
                            }}
                          />
                          {type} <span style={{ color: "#ccc", fontSize: 12 }}>({count})</span>

                          {isTypeExpanded && matchingPois.length > 0 && (
                            <ul style={{ marginLeft: 24, marginTop: 2 }}>
                              {matchingPois.map(poi => (
                                <li
                                  key={poi.id}
                                  className="sidebar-poi-item"
                                  onMouseEnter={() => setHoveredPoiId && setHoveredPoiId(poi.id)}
                                  onMouseLeave={() => setHoveredPoiId && setHoveredPoiId(null)}
                                  onClick={() => setHoveredPoiId && setHoveredPoiId(poi.id)}
                                >
                                  <div className="sidebar-poi-content">
                                    <span
                                      className="sidebar-poi-icon"
                                      style={{ backgroundColor: color }}
                                    />
                                    <span className="sidebar-poi-label">
                                      {poi.poi_name || "(unnamed)"}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}

          {pathZones.length > 0 && (
            <div
              style={{
                marginTop: 26,
                paddingTop: 10,
                borderTop: "1px solid #444",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 4,
                  cursor: "pointer",
                  fontFamily,
                }}
                onClick={togglePathsExpanded}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1em",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: fontColour,
                  }}
                >
                  <span>{pathsExpanded ? "▼" : "►"}</span>
                  {sectionNamePaths}
                  <span style={{ fontSize: 13, color: "#bbb" }}>({pathZones.length})</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAllPaths();
                  }}
                  style={getToggleButtonStyle(mapStyles, fontFamily)}
                >
                  {allPathsHidden ? "Show All" : "Hide All"}
                </button>
              </div>
              {pathsExpanded && (
                <ul
                  style={{
                    paddingLeft: 4,
                    marginTop: 4,
                    listStyle: "none",
                    fontFamily,
                  }}
                >
                  {pathZones.map(zone => {
                    const label = zone.label?.trim() || "Untitled Path";
                    const ctrl = pathControls[zone.id] || {};
                    const isPaused = !!ctrl.paused;
                    const speed =
                      typeof ctrl.speed === "number" ? ctrl.speed : 0.25;
                    const position =
                      typeof ctrl.position === "number"
                        ? Math.min(Math.max(ctrl.position, 0), 1)
                        : 0;
                    const tokenVisible = !(ctrl.hidden === true);

                    return (
                      <li
                        key={zone.id}
                        style={{
                          marginBottom: 8,
                          padding: "6px 6px 8px 4px",
                          borderRadius: 6,
                          background: "#23252d",
                        }}
                        onMouseEnter={() => setHoveredZoneId(zone.id)}
                        onMouseLeave={() => setHoveredZoneId(null)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 4,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginLeft: 2,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={tokenVisible}
                              onChange={() => togglePathVisibility(zone.id)}
                              style={{ marginRight: 2 }}
                            />
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 14,
                                color: fontColour,
                              }}
                              title={label}
                            >
                              {label}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handlePathControlChange(zone.id, {
                                paused: !isPaused,
                              })
                            }
                            style={{
                              fontSize: 11,
                              padding: "3px 9px",
                              borderRadius: 999,
                              border: "none",
                              cursor: "pointer",
                              background: isPaused ? "#3a7cff" : "#ffb347",
                              color: "#111",
                              fontWeight: 700,
                              marginRight: 4,
                              fontFamily,
                            }}
                          >
                            {isPaused ? "Resume" : "Pause"}
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginLeft: 4,
                            marginRight: 4,
                            marginBottom: 3,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              opacity: 0.8,
                              minWidth: 40,
                              color: "#ddd",
                            }}
                          >
                            Speed
                          </span>
                          <input
                            type="range"
                            min={0.05}
                            max={1}
                            step={0.05}
                            value={speed}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0.05;
                              handlePathControlChange(zone.id, {
                                speed: val,
                              });
                            }}
                            style={{ flex: 1 }}
                          />
                          <span
                            style={{
                              fontSize: 11,
                              width: 34,
                              textAlign: "right",
                              opacity: 0.85,
                              color: "#eee",
                            }}
                          >
                            {speed.toFixed(2)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginLeft: 4,
                            marginRight: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              opacity: 0.8,
                              minWidth: 50,
                              color: "#ddd",
                            }}
                          >
                            Position
                          </span>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(position * 100)}
                            onChange={(e) => {
                              const raw = parseInt(e.target.value, 10);
                              const pct = Number.isNaN(raw)
                                ? 0
                                : Math.min(Math.max(raw, 0), 100);
                              const normalized = pct / 100;
                              handlePathControlChange(zone.id, {
                                position: normalized,
                              });
                            }}
                            style={{ flex: 1 }}
                          />
                          <span
                            style={{
                              fontSize: 11,
                              width: 34,
                              textAlign: "right",
                              opacity: 0.85,
                              color: "#eee",
                            }}
                          >
                            {Math.round(position * 100)}%
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {normalZones.length > 0 && (
            <div style={{ marginTop: 28, marginBottom: 18 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontWeight: 700,
                  fontSize: "1.1em",
                  cursor: "pointer",
                  marginBottom: 4,
                  fontFamily: fontFamily,
                  color: fontColour,
                  letterSpacing: 0.1,
                  userSelect: 'none'
                }}
              >
                <div
                  onClick={toggleZonesExpand}
                  style={{ display: "flex", alignItems: "center" }}
                  tabIndex={0}
                  role="button"
                  aria-label={zonesExpanded ? 'Collapse' : 'Expand'}
                >
                  <span style={{ marginRight: 7 }}>
                    {zonesExpanded ? '▼' : '►'}
                  </span>
                  {sectionNameZones}
                  <span
                    style={{
                      marginLeft: 7,
                      fontSize: 14,
                      color: "#bbb",
                    }}
                  >
                    ({normalZones.length})
                  </span>
                </div>
                <button
                  onClick={toggleAllZones}
                  style={getToggleButtonStyle(mapStyles, fontFamily)}
                >
                  {allZonesVisible ? "Hide All" : "Show All"}
                </button>
              </div>
              {zonesExpanded && (
                <div style={{ paddingLeft: 16 }}>
                  <ul style={{ margin: 0, padding: 0 }}>
                    {normalZones.map(zone => (
                      <li
                        key={zone.id}
                        style={{
                          marginBottom: 2,
                          display: "flex",
                          alignItems: "center",
                          fontFamily: fontFamily,
                          fontWeight: 600,
                          fontSize: 15,
                          color: fontColour,
                          letterSpacing: 0.05
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visibleZones.has(zone.id)}
                          onChange={() => toggleZone(zone.id)}
                          style={{ marginRight: 6 }}
                        />
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            marginRight: 6,
                            color: fontColour,
                            fontFamily: fontFamily,
                            letterSpacing: 0.03,
                            cursor: "pointer",
                            transition: "color 0.14s",
                            ...(hoveredZoneId === zone.id
                              ? {
                                  textDecoration: "underline",
                                  color:
                                    mapStyles?.highlight_colour || "#ffe670",
                                }
                              : {})
                          }}
                          onMouseEnter={() => setHoveredZoneId(zone.id)}
                          onMouseLeave={() => setHoveredZoneId(null)}
                        >
                          {zone.label?.trim() || zone.name || `Zone ${zone.id}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          flex: "1 1 0%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
          fontFamily: fontFamily,
        }}
      >
        <InteractiveMap
          mapData={mapData}
          imageSize={imageSize}
          pois={pois}
          foundPOIs={[]}
          showFound={true}
          setShowFound={() => {}}
          visibleTypes={visibleTypes}
          visibleCategories={visibleCategories}
          hoveredType={hoveredType}
          hoveredPoiId={hoveredPoiId}
          setHoveredPoiId={setHoveredPoiId}
          pendingPOI={null}
          isMapOwnerOrFounder={false}
          handleMapCoordClick={() => {}}
          handleClick={() => {}}
          mouseCoord={null}
          handleMouseCoord={() => {}}
          shareCoord={null}
          handleCopyCoord={() => {}}
          copiedCoord={false}
          customCategories={customCategories}
          typeColours={typeColours}
          customTypes={customTypes}
          selectedCategory={""}
          setSelectedCategory={() => {}}
          selectedType={""}
          setSelectedType={() => {}}
          typeColourInput={"#ff0000"}
          setTypeColourInput={() => {}}
          shape={"circle"}
          setShape={() => {}}
          description={""}
          setDescription={() => {}}
          savePOI={() => {}}
          showAllPoiLabels={showAllPoiLabels}
          setShowAllPoiLabels={setShowAllPoiLabels}
          onToggleFound={() => {}}
          onCategoryUpdate={() => {}}
          user={null}
          mapOwnerId={mapData.uploader_id}
          isEmbed={true}
          styles={mapStyles}
          zones={zones}
          visibleZoneIds={
            visibleZones instanceof Set ? visibleZones : new Set(visibleZones)
          }
          setVisibleZones={setVisibleZones}
          toggleZone={toggleZone}
          toggleAllZones={toggleAllZones}
          zonesExpanded={zonesExpanded}
          toggleZonesExpand={toggleZonesExpand}
          hoveredZoneId={hoveredZoneId}
          setHoveredZoneId={setHoveredZoneId}
          pathControls={pathControls}
          onPathControlChange={handlePathControlChange}
        />
        <a
          href="https://point-atlas.io/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: "absolute",
            right: 13,
            bottom: 90,
            zIndex: 4000,
            opacity: 0.5,
            transition: "opacity 0.2s",
            display: "flex",
            alignItems: "center"
          }}
        >
          <img
            src={logo}
            alt="Point Atlas"
            style={{
              width: 27,
              height: 27,
              borderRadius: 8,
              boxShadow: "0 1px 4px #2224",
              border: "1px solid #666",
              background: "#fff"
            }}
            draggable={false}
          />
        </a>
      </div>
    </div>
  );
}

export default EmbedMap;
